/**
 * Tooling Gap Appendix Slide
 * Why the 50-50 split fails: cognitive load stats, domain table,
 * 3 structural barriers, enterprise comparison, California data
 * Sources: Aviv & Saxbe 2024, Weeks et al. 2025, Eve Rodsky,
 *          GEPI 2025, Pew Research, BLS 2024
 */

import React from 'react';
import { colors } from '@config/colors';
import {
  AppendixSlideLayout,
  AppendixContentArea,
  SlideHeader,
  AppendixColumn,
  AppendixSectionLabel,
  AppendixStatBar,
  AppendixStatNumber,
  AppendixStatDescription,
  AppendixStatSource,
  AppendixDataTable,
  AppendixDataRow,
  AppendixDataHeader,
  AppendixCellHighlight,
  AccentCalloutBox,
  AccentCalloutBody,
  AccentCalloutBold,
  AccentBorderedCard,
  AccentBorderedCardTitle,
  AccentBorderedCardBody,
  DarkInsightBox,
  DarkInsightTitle,
  DarkInsightBody,
  DarkInsightEmphasis,
  AppendixSourceFooter,
  ItalicCalloutBody,
  QuoteAttribution,
} from '@components/press/pitch-deck/slide-primitives';

// ============================================
// Component
// ============================================

export const ToolingGapSlide: React.FC = () => (
  <AppendixSlideLayout>
    <SlideHeader
      title="Why Can&#x2019;t Families Just Share It?"
      subtitle="The imbalance isn&#x2019;t a relationship problem — it&#x2019;s a tooling & infrastructure problem"
    />

    <AppendixContentArea>
      <AppendixColumn>
        <AppendixSectionLabel>The Cognitive Load Gap</AppendixSectionLabel>

        <AppendixStatBar>
          <AppendixStatNumber>73%</AppendixStatNumber>
          <AppendixStatDescription>
            of all cognitive household labor falls on mothers &mdash; even when
            both parents work full time. Physical gap is 64/36; the <em>thinking</em> gap
            is dramatically wider. <AppendixStatSource>(Aviv &amp; Saxbe, 2024 &mdash; 322 mothers, 30 tasks)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixStatBar>
          <AppendixStatNumber>79%</AppendixStatNumber>
          <AppendixStatDescription>
            of &ldquo;daily&rdquo; cognitive tasks &mdash; the constant mental tracking
            of schedules, meals, and supplies &mdash; are handled by mothers. More than 2&times;
            what fathers handle. <AppendixStatSource>(Weeks et al., 2025)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixStatBar>
          <AppendixStatNumber>72%</AppendixStatNumber>
          <AppendixStatDescription>
            longer &ldquo;second shift&rdquo; on workdays for working mothers vs. fathers.
            CA mothers spend ~37 hrs/wk on unpaid care and have 41% less free time.{' '}
            <AppendixStatSource>(GEPI, 2025)</AppendixStatSource>
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixStatBar>
          <AppendixStatNumber>35+</AppendixStatNumber>
          <AppendixStatDescription>
            consequential decisions per week spanning finances, health, nutrition,
            scheduling, education, and maintenance &mdash; with no planning cycle,
            no dashboard, no staff meeting. Decisions default to <em>fast</em> over <em>good</em>.
          </AppendixStatDescription>
        </AppendixStatBar>

        <AppendixSectionLabel>Who&rsquo;s Responsible?</AppendixSectionLabel>
        <AppendixDataTable>
          <AppendixDataHeader $columns="1.6fr 0.5fr 0.5fr 0.5fr">
            <div>Domain</div>
            <div>Mom</div>
            <div>Shared</div>
            <div>Dad</div>
          </AppendixDataHeader>
          <AppendixDataRow $columns="1.6fr 0.5fr 0.5fr 0.5fr">
            <div>Kids&rsquo; schedules &amp; activities</div>
            <AppendixCellHighlight>59%</AppendixCellHighlight>
            <div>36%</div>
            <div>5%</div>
          </AppendixDataRow>
          <AppendixDataRow $columns="1.6fr 0.5fr 0.5fr 0.5fr">
            <div>Sick child care</div>
            <AppendixCellHighlight>55%</AppendixCellHighlight>
            <div>41%</div>
            <div>4%</div>
          </AppendixDataRow>
          <AppendixDataRow $columns="1.6fr 0.5fr 0.5fr 0.5fr">
            <div>Scheduling appointments</div>
            <AppendixCellHighlight>~70%</AppendixCellHighlight>
            <div>~15%</div>
            <div>~15%</div>
          </AppendixDataRow>
          <AppendixDataRow $columns="1.6fr 0.5fr 0.5fr 0.5fr">
            <div>Household chores overall</div>
            <AppendixCellHighlight>41%</AppendixCellHighlight>
            <div>50%</div>
            <div>8%</div>
          </AppendixDataRow>
          <AppendixDataRow $columns="1.6fr 0.5fr 0.5fr 0.5fr">
            <div>Managing children&rsquo;s schedules (dual FT)</div>
            <AppendixCellHighlight>54%</AppendixCellHighlight>
            <div>&mdash;</div>
            <div>&mdash;</div>
          </AppendixDataRow>
        </AppendixDataTable>

        <AccentCalloutBox $bg={colors.accent.blue.extraLight} $accent={colors.accent.blue.exxtraDark}>
          <AccentCalloutBody>
            <AccentCalloutBold>The enterprise comparison:</AccentCalloutBold> A company spending $145K
            on an employee&rsquo;s output gives them an <AccentCalloutBold>$8,700/yr tech stack</AccentCalloutBold>.
            Moms get a shared Google Calendar. No enterprise would run like this.
          </AccentCalloutBody>
        </AccentCalloutBox>
      </AppendixColumn>

      <AppendixColumn>
        <AppendixSectionLabel>3 Structural Barriers to Equal Sharing</AppendixSectionLabel>

        <AccentBorderedCard $accent={colors.accent.coral.exxtraDark}>
          <AccentBorderedCardTitle>1. Invisible &amp; Illegible</AccentBorderedCardTitle>
          <AccentBorderedCardBody>
            No shared system makes the full scope visible. You can see who folds laundry;
            you can&rsquo;t see who tracks the booster shot deadline (6 wks out), the
            pediatrician booking window (3 wks out), and the kindergarten form due date &mdash;
            simultaneously. Without a system of record, there&rsquo;s nothing to hand off,
            delegate, or split.
          </AccentBorderedCardBody>
        </AccentBorderedCard>

        <AccentBorderedCard $accent={colors.accent.blue.exxtraDark}>
          <AccentBorderedCardTitle>2. Deeply Contextual &amp; Interconnected</AccentBorderedCardTitle>
          <AccentBorderedCardBody>
            Household decisions are a dependency graph, not independent tasks. Changing soccer
            to Thursday affects meal prep &rarr; grocery timing &rarr; budget &rarr; sibling
            pickup. This context lives in one head because no tool models these relationships.
            Delegation without context transfer creates <em>more</em> coordination overhead
            for the person holding the mental model.
          </AccentBorderedCardBody>
        </AccentBorderedCard>

        <AccentBorderedCard $accent={colors.accent.purple.exxtraDark}>
          <AccentBorderedCardTitle>3. Tools Fragment, Not Unify</AccentBorderedCardTitle>
          <AccentBorderedCardBody>
            Families use 5&ndash;12 apps: calendar, grocery, budget, school portal, medical
            portal, chore chart. Every tool is a silo. None talk to each other. None of
            them <em>think</em>. The only &ldquo;integration layer&rdquo; is mom&rsquo;s
            working memory &mdash; running at 100% utilization.
          </AccentBorderedCardBody>
        </AccentBorderedCard>

        <AccentCalloutBox $bg={colors.accent.yellow.extraLight} $accent={colors.accent.yellow.exxtraDark}>
          <ItalicCalloutBody>
            &ldquo;What do you need me to do?&rdquo; &mdash; which, as <QuoteAttribution>Eve
            Rodsky</QuoteAttribution> documented, just adds another task to mom&rsquo;s queue:
            figuring out what to delegate. Even well-intentioned partners can&rsquo;t absorb
            cognitive labor because the knowledge is tacit, the tools are fragmented, and
            there is no &ldquo;operating system&rdquo; for the household.
          </ItalicCalloutBody>
        </AccentCalloutBox>

        <DarkInsightBox>
          <DarkInsightTitle>AI Changes the Equation</DarkInsightTitle>
          <DarkInsightBody>
            An intelligent system can <DarkInsightEmphasis>hold the household context</DarkInsightEmphasis>,
            anticipate needs, surface upcoming decisions, and make the invisible visible &mdash;
            giving <em>both</em> partners access to the institutional knowledge that currently
            lives exclusively in mom&rsquo;s head. It&rsquo;s the cognitive load, not the
            physical labor, that&rsquo;s most strongly associated with maternal depression,
            burnout, and relationship breakdown.
          </DarkInsightBody>
        </DarkInsightBox>
      </AppendixColumn>
    </AppendixContentArea>

    <AppendixSourceFooter>
      Aviv &amp; Saxbe (2024) &middot; Weeks, Kowalewska &amp; Ruppanner (2025) &middot;
      GEPI &ldquo;When Time Isn&rsquo;t on Your Side&rdquo; (2025) &middot;
      Pew Research (2023) &middot; BLS Employment Characteristics (2024) &middot;
      Productiv/Zylo enterprise SaaS estimates
    </AppendixSourceFooter>
  </AppendixSlideLayout>
);
